import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { User, Mail, MapPin, Phone, Building2, Edit2, Save, Calendar, TrendingUp, DollarSign, Star } from "lucide-react";

const VenueOwnerProfile = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [venues, setVenues] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({
    name: "",
    email: "",
    phone: "",
    location: "",
    city: "",
    district: "",
    bio: "",
    profileImage: ""
  });

  useEffect(() => {
    if (user?.role !== "venue_owner") {
      navigate("/");
      return;
    }
    fetchProfile();
    fetchVenues();
    fetchStats();
  }, [user]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setProfile(response.data);
      setEditData({
        name: response.data.name || "",
        email: response.data.email || "",
        phone: response.data.phone || "",
        location: response.data.location || "",
        city: response.data.city || "",
        district: response.data.district || "",
        bio: response.data.bio || "",
        profileImage: response.data.profileImage || ""
      });
    } catch (error) {
      console.error("Error fetching profile:", error);
      toast.error("Profil yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const fetchVenues = async () => {
    try {
      const response = await axios.get(`${API}/venue-owner/venues`);
      setVenues(response.data);
    } catch (error) {
      console.error("Error fetching venues:", error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/venue-owner/dashboard`);
      setStats(response.data.stats);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const handleSave = async () => {
    try {
      await axios.put(`${API}/users/profile`, editData);
      toast.success("Profil güncellendi");
      setEditing(false);
      fetchProfile();
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Profil güncellenemedi");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Navbar user={user} onLogout={onLogout} />

      <div className="container mx-auto px-4 py-8">
        {/* Header Card */}
        <Card className="mb-6 border-l-4 border-l-blue-500">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-6">
                <Avatar className="h-24 w-24 border-4 border-blue-500">
                  <AvatarImage src={profile?.profileImage} />
                  <AvatarFallback className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white text-2xl">
                    {profile?.name?.charAt(0) || "S"}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h1 className="text-3xl font-bold text-gray-800 mb-1">{profile?.name}</h1>
                  <div className="flex items-center gap-2 text-gray-600 mb-2">
                    <Building2 className="h-4 w-4" />
                    <span className="font-semibold">Saha Sahibi</span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <div className="flex items-center gap-1">
                      <Mail className="h-4 w-4" />
                      {profile?.email}
                    </div>
                    {profile?.phone && (
                      <div className="flex items-center gap-1">
                        <Phone className="h-4 w-4" />
                        {profile.phone}
                      </div>
                    )}
                    {profile?.location && (
                      <div className="flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        {profile.location}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <Button onClick={() => setEditing(true)} variant="outline">
                <Edit2 className="h-4 w-4 mr-2" />
                Profili Düzenle
              </Button>
            </div>
            {profile?.bio && (
              <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                <p className="text-gray-700">{profile.bio}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Stats Cards */}
        {stats && (
          <div className="grid md:grid-cols-4 gap-4 mb-6">
            <Card className="border-l-4 border-l-blue-500">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm text-gray-600">Toplam Saha</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-blue-600">{stats.totalVenues || 0}</div>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-green-500">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm text-gray-600">Toplam Rezervasyon</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-600">{stats.totalBookings || 0}</div>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-purple-500">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm text-gray-600">Aylık Gelir</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-purple-600">{stats.monthlyRevenue || 0} TL</div>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-orange-500">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm text-gray-600">Ortalama Puan</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <Star className="h-6 w-6 text-orange-500 fill-orange-500" />
                  <div className="text-3xl font-bold text-orange-600">4.5</div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <Tabs defaultValue="venues" className="space-y-6">
          <TabsList>
            <TabsTrigger value="venues">Sahalarım ({venues.length})</TabsTrigger>
            <TabsTrigger value="info">Hesap Bilgileri</TabsTrigger>
          </TabsList>

          {/* Venues Tab */}
          <TabsContent value="venues">
            <div className="grid md:grid-cols-2 gap-4">
              {venues.length === 0 ? (
                <Card className="col-span-2">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <Building2 className="h-16 w-16 text-gray-300 mb-4" />
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">Henüz Saha Eklemediniz</h3>
                    <p className="text-gray-500 mb-4">İlk sahanızı ekleyerek başlayın</p>
                    <Button onClick={() => navigate("/venues")}>
                      Saha Ekle
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                venues.map((venue) => (
                  <Card key={venue.id} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-xl">{venue.name}</CardTitle>
                          <CardDescription className="flex items-center gap-1 mt-1">
                            <MapPin className="h-4 w-4" />
                            {venue.location}
                          </CardDescription>
                        </div>
                        <Badge variant="outline" className="bg-blue-50">
                          {venue.format || "7x7"}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {venue.images && venue.images.length > 0 && (
                        <img
                          src={venue.images[0]}
                          alt={venue.name}
                          className="w-full h-40 object-cover rounded-lg mb-4"
                        />
                      )}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Kapasite:</span>
                          <span className="font-semibold">{venue.capacity} kişi</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Saat Başı Fiyat:</span>
                          <span className="font-semibold text-blue-600">{venue.pricePerHour} TL</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Puan:</span>
                          <div className="flex items-center gap-1">
                            <Star className="h-4 w-4 text-orange-500 fill-orange-500" />
                            <span className="font-semibold">{venue.rating || 4.5}</span>
                          </div>
                        </div>
                      </div>
                      <div className="mt-4 flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => navigate(`/venue-settings/${venue.id}`)}
                        >
                          Düzenle
                        </Button>
                        <Button
                          size="sm"
                          className="flex-1"
                          onClick={() => navigate(`/reservation-calendar?venueId=${venue.id}`)}
                        >
                          Rezervasyonlar
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Account Info Tab */}
          <TabsContent value="info">
            <Card>
              <CardHeader>
                <CardTitle>Hesap Bilgileri</CardTitle>
                <CardDescription>Kişisel bilgileriniz ve iletişim detayları</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-600">Ad Soyad</Label>
                    <p className="font-semibold">{profile?.name}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">E-posta</Label>
                    <p className="font-semibold">{profile?.email}</p>
                  </div>
                  {profile?.phone && (
                    <div>
                      <Label className="text-gray-600">Telefon</Label>
                      <p className="font-semibold">{profile.phone}</p>
                    </div>
                  )}
                  {profile?.city && (
                    <div>
                      <Label className="text-gray-600">Şehir</Label>
                      <p className="font-semibold">{profile.city}</p>
                    </div>
                  )}
                  {profile?.district && (
                    <div>
                      <Label className="text-gray-600">İlçe</Label>
                      <p className="font-semibold">{profile.district}</p>
                    </div>
                  )}
                  <div>
                    <Label className="text-gray-600">Rol</Label>
                    <Badge className="bg-blue-500">Saha Sahibi</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit Profile Dialog */}
      <Dialog open={editing} onOpenChange={setEditing}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Profili Düzenle</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Ad Soyad</Label>
                <Input
                  value={editData.name}
                  onChange={(e) => setEditData({...editData, name: e.target.value})}
                  placeholder="Adınız Soyadınız"
                />
              </div>
              <div>
                <Label>E-posta</Label>
                <Input
                  type="email"
                  value={editData.email}
                  onChange={(e) => setEditData({...editData, email: e.target.value})}
                  placeholder="ornek@email.com"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Telefon</Label>
                <Input
                  value={editData.phone}
                  onChange={(e) => setEditData({...editData, phone: e.target.value})}
                  placeholder="05XX XXX XX XX"
                />
              </div>
              <div>
                <Label>Konum</Label>
                <Input
                  value={editData.location}
                  onChange={(e) => setEditData({...editData, location: e.target.value})}
                  placeholder="İstanbul, Türkiye"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Şehir</Label>
                <Input
                  value={editData.city}
                  onChange={(e) => setEditData({...editData, city: e.target.value})}
                  placeholder="İstanbul"
                />
              </div>
              <div>
                <Label>İlçe</Label>
                <Input
                  value={editData.district}
                  onChange={(e) => setEditData({...editData, district: e.target.value})}
                  placeholder="Kadıköy"
                />
              </div>
            </div>

            <div>
              <Label>Profil Fotoğrafı URL</Label>
              <Input
                value={editData.profileImage}
                onChange={(e) => setEditData({...editData, profileImage: e.target.value})}
                placeholder="https://example.com/photo.jpg"
              />
            </div>

            <div>
              <Label>Biyografi</Label>
              <Textarea
                value={editData.bio}
                onChange={(e) => setEditData({...editData, bio: e.target.value})}
                placeholder="Kendinizden bahsedin..."
                rows={4}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(false)}>İptal</Button>
            <Button onClick={handleSave}>
              <Save className="h-4 w-4 mr-2" />
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VenueOwnerProfile;
